import { applyCommissionFieldsToRecord } from "./propertyCommission";
import { readListingKeyLocationPassword } from "./visitKeyLocation";

export interface PropertyListRowFields {
  address: string;
  neighborhood: string;
  number: string;
  floor?: string | null;
  complement?: string | null;
  cep?: string | null;
  zip_code?: string | null;
  rooms?: number | null;
  bathrooms?: number | null;
  garages?: number | null;
  area?: number | string | null;
  property_type?: { name?: string; label?: string } | string | null;
  type?: string | null;
  type_name?: string | null;
  property_type_name?: string | null;
  status?: string | null;
  is_draft?: boolean;
  key_location?: { id?: number; name?: string; slug?: string } | string | null;
  key_location_id?: number | null;
  key_location_note?: string | null;
  key_location_password?: string | null;
  commission_type?: string | null;
  admin_percentage?: number | string | null;
  first_rent_commission_7cantos_percentage?: number | string | null;
  partner_name?: string | null;
  partner_first_rent_commission_percentage?: number | string | null;
  commission_estimate?: unknown;
}

export function getPropertyTypeLabel(property: PropertyListRowFields): string {
  if (property.type != null && String(property.type).trim() !== "") {
    return String(property.type).trim();
  }
  const raw = property.property_type;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (raw && typeof raw === "object" && raw !== null && "name" in raw) {
    const n = (raw as { name?: unknown }).name;
    if (typeof n === "string" && n.trim()) return n.trim();
  }
  if (raw && typeof raw === "object" && raw !== null && "label" in raw) {
    const n = (raw as { label?: unknown }).label;
    if (typeof n === "string" && n.trim()) return n.trim();
  }
  if (property.type_name != null && String(property.type_name).trim() !== "") {
    return String(property.type_name).trim();
  }
  if (
    property.property_type_name != null &&
    String(property.property_type_name).trim() !== ""
  ) {
    return String(property.property_type_name).trim();
  }
  return "";
}

export function buildPropertyAddressPrimaryLine(
  property: PropertyListRowFields
): string {
  const street = (property.address ?? "").trim();
  const number = (property.number ?? "").trim();
  const floor =
    property.floor != null && String(property.floor).trim() !== ""
      ? String(property.floor).trim()
      : "";
  const complement =
    property.complement != null && String(property.complement).trim() !== ""
      ? String(property.complement).trim()
      : "";
  const mid = floor || complement;
  const neighborhood = (property.neighborhood ?? "").trim();
  const streetNumber = [street, number].filter(Boolean).join(", ");
  const segments = [streetNumber];
  if (mid) segments.push(mid);
  if (neighborhood) segments.push(neighborhood);
  const joined = segments.filter((s) => s !== "").join(" — ");
  return joined !== "" ? joined : "—";
}

export function formatCepLine(cep: string | null | undefined): string {
  if (cep == null || String(cep).trim() === "") return "—";
  const digits = String(cep).replace(/\D/g, "");
  if (digits.length === 8) {
    return `CEP ${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  return `CEP ${String(cep).trim()}`;
}

export function formatAreaForList(area: number | string | null | undefined): string {
  if (area == null || area === "") return "—";
  if (typeof area === "number" && Number.isFinite(area)) {
    return `${area}m²`;
  }
  const cleaned = String(area)
    .replace(/m²/gi, "")
    .replace(",", ".")
    .trim();
  const n = Number(cleaned);
  if (Number.isFinite(n)) {
    return `${n}m²`;
  }
  const t = String(area).trim();
  return t.toLowerCase().includes("m²") ? t : `${t}m²`;
}

export function formatCountForList(
  value: number | null | undefined
): string {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return String(Number(value));
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function firstFiniteNumber(...candidates: unknown[]): number | undefined {
  for (const c of candidates) {
    if (c === null || c === undefined || c === "") continue;
    const n = Number(c);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function stringish(v: unknown): string {
  if (v == null) return "";
  return String(v).trim();
}

/**
 * Normalizes `/api/adm/properties` index rows: nested `address` objects and
 * alternate field names are flattened so list formatters receive consistent shapes.
 */
export function toPropertyListRowFields(
  rawInput: Record<string, unknown>
): PropertyListRowFields {
  const nestedProperty =
    rawInput.property &&
    typeof rawInput.property === "object" &&
    !Array.isArray(rawInput.property)
      ? (rawInput.property as Record<string, unknown>)
      : null;
  const raw = nestedProperty ? { ...rawInput, ...nestedProperty } : rawInput;
  const addr = raw.address;
  let street = "";
  let number = stringish(raw.number);
  let neighborhood = stringish(raw.neighborhood);
  let floor: string | null | undefined = raw.floor != null ? stringish(raw.floor) : undefined;
  let complement: string | null | undefined =
    raw.complement != null ? stringish(raw.complement) : undefined;
  let cep: string | null | undefined =
    raw.cep != null ? stringish(raw.cep) : undefined;
  let zipCode: string | null | undefined =
    raw.zip_code != null ? stringish(raw.zip_code) : undefined;

  if (typeof addr === "string") {
    street = addr.trim();
  } else if (isPlainObject(addr)) {
    const a = addr;
    street =
      (typeof a.address === "string" && a.address.trim()) ||
      (typeof a.street === "string" && a.street.trim()) ||
      "";
    number = stringish(a.number ?? raw.number);
    neighborhood = stringish(a.neighborhood ?? raw.neighborhood);
    if (a.floor != null) floor = stringish(a.floor);
    if (a.complement != null) complement = stringish(a.complement);
    const nestedCep = a.cep ?? a.zip_code;
    if (nestedCep != null) {
      const c = stringish(nestedCep);
      cep = c || undefined;
      zipCode = c || zipCode;
    }
  }

  const rooms = firstFiniteNumber(
    raw.rooms,
    raw.bedrooms,
    raw.room,
    raw.bedroom
  );
  const bathrooms = firstFiniteNumber(
    raw.bathrooms,
    raw.bathroom,
    raw.bath
  );
  const garages = firstFiniteNumber(
    raw.garages,
    raw.garage,
    raw.parking_spaces,
    raw.parking
  );

  const area =
    raw.area ?? raw.useful_area ?? raw.usefulArea ?? raw.built_area ?? raw.total_area;

  const base = { ...raw } as unknown as PropertyListRowFields;

  const addressLine =
    street ||
    (typeof raw.address === "string" ? stringish(raw.address) : "");

  return applyCommissionFieldsToRecord({
    ...base,
    address: addressLine,
    number: number || stringish(raw.number),
    neighborhood: neighborhood || stringish(raw.neighborhood),
    floor: floor ?? base.floor,
    complement: complement ?? base.complement,
    cep: cep ?? base.cep,
    zip_code: zipCode ?? base.zip_code,
    rooms: rooms ?? base.rooms ?? null,
    bathrooms: bathrooms ?? base.bathrooms ?? null,
    garages: garages ?? base.garages ?? null,
    area: (area ?? base.area) as number | string | null | undefined,
    type: (raw.type != null ? stringish(raw.type) : base.type) || null,
    property_type: (raw.property_type ?? base.property_type) as
      | PropertyListRowFields["property_type"]
      | undefined,
    type_name: (raw.type_name ?? base.type_name) as string | null | undefined,
    property_type_name: (raw.property_type_name ??
      base.property_type_name) as string | null | undefined,
    status: (raw.status != null ? stringish(raw.status) : null) || null,
    is_draft: Boolean(raw.is_draft),
    key_location: raw.key_location ?? base.key_location,
    key_location_id:
      raw.key_location_id ??
      (isPlainObject(raw.key_location) ? raw.key_location.id : undefined) ??
      base.key_location_id,
    key_location_note: raw.key_location_note ?? base.key_location_note,
    key_location_password: readListingKeyLocationPassword(raw),
    commission_estimate: raw.commission_estimate ?? raw.commissionEstimate,
  }) as PropertyListRowFields;
}
